import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { fetchVideos } from "../../Redux/VideoUpload";
import MainPlayer from "./MainPlayer";
import EpisodeList from "./EpisodeList";
// import { videosList } from "./videosList";
import { useSelector } from "react-redux";
import { Typography, Box, Chip } from "@mui/material";
import OtherTalksSection from "./OtherTalksSection";
import NominateIgniteTalkSection from "./NominateIgniteTalkSection";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideoLibraryIcon from "@mui/icons-material/VideoLibrary";
import EventIcon from "@mui/icons-material/Event";
import "./IgniteTalksContent.css";
import { igniteTalkAssets } from "../../imagesData/ImagesData";
import useFetchImageUrls from "../../ConstantUseFetchImageUrls";
import axios from "axios";
import { BASE_API_URL } from "../../utils/constants";

const PLAYER_EPISODE_HEIGHT = 320;

const IgniteVideoContent = () => {
  const [activeTalk, setActiveTalk] = useState(null);
  const [activeEpisode, setActiveEpisode] = useState(null);
  const dispatch = useDispatch();
  const { uploadedVideos } = useSelector((state) => state.VideoUpload);
  const lastTrackedRef = useRef(null);

  const topRef = useRef(null);

  const query = useMemo(
    () => `params=${encodeURIComponent(JSON.stringify(igniteTalkAssets))}`,
    [],
  );
  const { imageUrls, error } = useFetchImageUrls(query);

  const trackVideo = (title) => {
    axios
      .post(`${BASE_API_URL}/track-video`, {
        enterprise_id: localStorage.getItem("user"),
        video_title: title,
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (!activeEpisode || !activeTalk) return;

    const title = activeEpisode.title || `${activeTalk.title} - Episode`;

    if (lastTrackedRef.current === title) return; // ✅ prevent repeat

    lastTrackedRef.current = title;

    trackVideo(title);
  }, [activeEpisode, activeTalk]);

  const transformedTalks = useMemo(() => {
    return (uploadedVideos || []).map((session) => ({
      ...session,
      episodes: (session.episodes || []).map((ep) => ({
        ...ep,
        url: ep.videoUrl || ep.url || ep.video_url,
        thumbnail: ep.thumbnailUrl || ep.thumbnail || ep.thumbnail_url,
      })),
    }));
  }, [uploadedVideos]);

  useEffect(() => {
    dispatch(fetchVideos());
  }, [dispatch]);

  useEffect(() => {
    if (!transformedTalks.length) return; // ✅ guard

    const sortedTalks = [...transformedTalks].sort((a, b) => {
      return new Date(b.courseDate) - new Date(a.courseDate);
    });

    const latestTalk = sortedTalks[0];

    if (!latestTalk) return;

    setActiveTalk(latestTalk);
    setActiveEpisode(latestTalk.episodes?.[0] || null);
  }, [transformedTalks]);

  if (error) return <div>Error loading assets.</div>;
  const handleTalkSelect = (talk) => {
    setActiveTalk(talk);
    setActiveEpisode(talk.episodes[0]);

    topRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  if (Object.keys(imageUrls).length === 0) return null;

  const rawThumb = activeEpisode?.thumbnail || activeTalk?.thumbnail;

  const thumbnail = !rawThumb
    ? null
    : rawThumb.startsWith("data:") || rawThumb.startsWith("http")
      ? rawThumb // ✅ already a full URL (DB video SAS or base64)
      : imageUrls?.imageUrl?.[rawThumb]; // ✅ static asset key lookup

  const rawVideo = activeEpisode?.url;

  const videoUrl = !rawVideo
    ? null
    : rawVideo.startsWith("http")
      ? rawVideo
      : imageUrls?.imageUrl?.[rawVideo];

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");
    const month = date.toLocaleString("default", { month: "long" });
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  return (
    <>
      {!activeTalk ? (
        <Box
          sx={{
            backgroundColor: "#250854",
            p: 5,
            textAlign: "center",
            minHeight: 300,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <Typography sx={{ color: "#fff", fontSize: 20, fontWeight: 600 }}>
            No Ignite Talks available yet.
          </Typography>
        </Box>
      ) : (
        <Box ref={topRef} sx={{ backgroundColor: "#250854", p: 3 }}>
          <Typography
            sx={{
              color: "#fff",
              display: "flex",
              alignItems: "center",
              mb: 1,
            }}
          >
            <FiberManualRecordIcon
              sx={{ color: "#F9F506", width: 10, mr: 1 }}
            />
            Most Popular Ignite Talks
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.5fr 1.5fr",
              gap: 6,
              mb: 3,
            }}
            className="ignite-talks-firstwrap"
          >
            <Box>
              <Typography sx={{ color: "#fff" }} className="active-talk-title">
                {activeTalk.title}
              </Typography>

              <Chip
                label={activeTalk.category}
                sx={{
                  backgroundColor: "#EEE6FF",
                  color: "#250854",
                  fontWeight: 600,
                  mt: 1,
                  borderRadius: 0,
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  color: "#ddd",
                  fontSize: 13,
                  mt: 2,
                }}
                className="ignite-talks-timings-box"
              >
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <AccessTimeIcon sx={{ fontSize: 16 }} />
                  {activeTalk.duration} Minutes
                </Box>

                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <VideoLibraryIcon sx={{ fontSize: 16 }} />
                  {activeTalk.episodes.length} Recordings
                </Box>

                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <EventIcon sx={{ fontSize: 16 }} />
                  {formatDate(activeTalk.courseDate)}
                </Box>
              </Box>
            </Box>

            {/* <Box sx={{ color: "#ddd", maxWidth: 520, position: "relative" }}> */}
            <Box sx={{ color: "#ddd", position: "relative" }}>
              <Box
                component="img"
                // src={imageUrls?.[activeTalk.speakerImage]}
                src={
                  activeTalk.speakerImage?.startsWith("http")
                    ? activeTalk.speakerImage
                    : "/Assets/Avatar.png"
                  // : imageUrls?.imageUrl?.[activeTalk.speakerImage]
                }
                alt={activeTalk.speakerName}
                sx={{
                  width: 45,
                  height: 45,
                  borderRadius: "50%",
                  objectFit: "cover",
                  position: "absolute",
                  left: -60,
                }}
                className="ignite-talks-speakerimg"
              />

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
                className="ignite-talks-speaker-textbox"
              >
                <Typography sx={{ fontSize: 13, opacity: 0.9 }}>
                  Speaker of the session:
                </Typography>

                <Typography
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: 14,
                  }}
                >
                  {activeTalk.speakerName
                    ?.toLowerCase()
                    .split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")}
                </Typography>
              </Box>

              <Typography
                sx={{
                  fontSize: 12,
                  lineHeight: 1.7,
                  color: "#E0D7FF",
                }}
              >
                {activeTalk.courseDescription}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{ display: "flex", gap: 2 }}
            className="ignite-talks-videos-box"
          >
            <Box sx={{ flex: 2.4, height: PLAYER_EPISODE_HEIGHT }}>
              <MainPlayer
                video={{
                  url: videoUrl,
                  thumbnail: thumbnail,
                }}
                isVideoSelected={!!activeEpisode}
                onPlayClick={() => {
                  if (!activeEpisode && activeTalk.episodes.length) {
                    setActiveEpisode(activeTalk.episodes[0]);
                  }
                }}
              />
            </Box>

            <Box
              sx={{ flex: 2, height: PLAYER_EPISODE_HEIGHT }}
              className="ignite-talks-seprate-videos-box"
            >
              <EpisodeList
                episodes={activeTalk.episodes}
                activeEpisode={activeEpisode}
                onSelect={setActiveEpisode}
                imageUrls={imageUrls?.imageUrl}
              />
            </Box>
          </Box>
        </Box>
      )}

      <OtherTalksSection
        talks={transformedTalks}
        activeTalkId={activeTalk?.id}
        onSelectTalk={handleTalkSelect}
        imageUrls={imageUrls?.imageUrl}
      />

      <NominateIgniteTalkSection />
    </>
  );
};

export default IgniteVideoContent;
